import {
  EYEBROW,
  FOCUS_RING,
  HOVER_T,
  ROW_HOVER,
} from "@/components/rail-kit/registers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils";
import { ChevronLeft, Search } from "lucide-react";
import React from "react";

/**
 * The manage-pane kit — the grammar every pane inside the Manage modal
 * composes from (design system: `specs/manage-modal`, the dashboard comp's
 * `manage-panes.jsx`). Hover/focus/eyebrow treatments come from
 * `rail-kit/registers` so nothing here re-states the ladder.
 */

const focusInset = FOCUS_RING;

const rowHover = ROW_HOVER;

/** Stacking rung for a dialog opened from inside the Manage modal. */
export const NESTED_DIALOG_Z = "z-[1000]";

export function GroupLabel({ children }: { children: React.ReactNode }) {
  return <div className={cn("px-2.5 pb-1", EYEBROW)}>{children}</div>;
}

export function Row({
  tile,
  frameless,
  dashed,
  name,
  meta,
  onOpen,
  children,
}: {
  tile: React.ReactNode;
  frameless?: boolean;
  dashed?: boolean;
  name: React.ReactNode;
  meta?: React.ReactNode;
  onOpen?: () => void;
  children?: React.ReactNode;
}) {
  const body = (
    <>
      {frameless ? (
        tile
      ) : (
        <span
          className={cn(
            "inline-flex size-9 shrink-0 items-center justify-center rounded-md border bg-n-05",
            dashed
              ? "border-dashed border-n-20 text-n-40"
              : "border-n-10 text-n-50",
          )}
        >
          {tile}
        </span>
      )}
      {/* `pr-4` on top of the row's gap: truncation otherwise lands flush
          against the trailing control, so a long archived title ran right up
          to its Restore button. This reserves the gutter instead. */}
      <span className="min-w-0 flex-1 pr-4">
        <span className="block truncate text-md font-medium text-n-90">
          {name}
        </span>
        {meta ? (
          <span className="block truncate text-sm text-n-50">{meta}</span>
        ) : null}
      </span>
    </>
  );

  return (
    <div
      className={cn(
        "group flex min-h-12 items-center gap-3 rounded-md px-2.5 py-2 transition-colors",
        onOpen && rowHover,
      )}
    >
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            "flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-md text-left",
            focusInset,
          )}
        >
          {body}
        </button>
      ) : (
        body
      )}
      {children}
    </div>
  );
}

export function BackRow({
  label,
  onBack,
}: {
  label: string;
  onBack: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onBack}
      className={cn(
        smButtonClassName,
        // No negative margin. The design system pulls this 4px left so the
        // chevron optically aligns with the content below, but the pane is a
        // scroll container — anything outside its content box is clipped, so
        // the trick cost the button its left edge and read as misaligned
        // against the masthead. Box-aligned instead.
        "self-start text-n-60 hover:text-n-90",
      )}
    >
      <ChevronLeft />
      {label}
    </Button>
  );
}

export function GroupTile({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md border border-n-10 bg-n-05 font-medium text-n-70",
        className,
      )}
    >
      {name?.[0]?.toUpperCase() ?? "?"}
    </span>
  );
}

export function DirtyField({
  label,
  initial,
  onSave,
  onChange,
  isSaving,
}: {
  label: string;
  initial: string;
  onSave: (value: string) => void | Promise<void>;
  /** Mirrors each keystroke out, for callers rendering a live preview. */
  onChange?: (value: string) => void;
  isSaving?: boolean;
}) {
  const id = React.useId();
  const [saved, setSaved] = React.useState(initial);
  const [val, setVal] = React.useState(initial);
  const dirty = val !== saved;

  React.useEffect(() => {
    setSaved(initial);
    setVal(initial);
  }, [initial]);

  return (
    <div className="px-2.5">
      <label htmlFor={id} className="text-sm font-medium text-n-70">
        {label}
      </label>
      <div className="mt-1.5 flex items-center gap-2">
        <Input
          id={id}
          value={val}
          onChange={(e) => {
            setVal(e.target.value);
            onChange?.(e.target.value);
          }}
          className={cn(paneFieldInputClassName, "max-w-80")}
        />
        {dirty ? (
          <Button
            size="sm"
            variant="secondary"
            className={smButtonClassName}
            disabled={isSaving}
            isLoading={isSaving}
            onClick={async () => {
              // Only a save that resolves counts as committed — otherwise the
              // field would go quiet while still holding unsaved text.
              try {
                await onSave(val);
                setSaved(val);
              } catch {
                // The caller surfaces the failure; the field stays dirty.
              }
            }}
          >
            Save
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Label + description on the left, live control(s) on the right. No hover
 * wash — acknowledgment belongs to the control, not the row.
 */
export function SettingRow({
  label,
  desc,
  children,
}: {
  label: React.ReactNode;
  desc?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-10 items-center gap-3 px-2.5 py-1.5">
      <div className="min-w-0 flex-1">
        <div className="text-md text-n-90">{label}</div>
        {desc ? <div className="mt-0.5 text-sm text-n-50">{desc}</div> : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

/**
 * The in-row select register: a compact trigger sized to the row rather than
 * the form. `borderless` is the inline form used for per-row controls.
 */
export function SelectSm({
  value,
  onValueChange,
  ariaLabel,
  placeholder,
  borderless,
  /**
   * `row` is the compact control that sits beside a SettingRow's label;
   * `field` matches the text inputs it is stacked with under a field label,
   * so a select never reads as a shrunken sibling.
   */
  size = "row",
  className,
  children,
}: {
  value?: string;
  onValueChange?: (value: string) => void;
  ariaLabel?: string;
  placeholder?: string;
  borderless?: boolean;
  size?: "row" | "field";
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label={ariaLabel}
        className={cn(
          "w-44 shrink-0 bg-input-background [&>span]:truncate",
          // Both rungs carry the same padding as the text input they stand
          // beside (`paneInputClassName` / `paneFieldInputClassName`), so a
          // select and a field on one row always agree on height. `h-auto` is
          // load-bearing: a fixed `h-*` would clamp the content box instead.
          size === "row"
            ? "h-auto min-h-8 px-2.5 py-2 text-sm [&>svg]:size-3"
            : "h-auto min-h-9 px-3 py-2 text-base [&>svg]:size-3.5",
          borderless &&
            "w-auto gap-1.5 border-transparent bg-transparent px-2 text-n-60 shadow-none hover:text-n-90 focus:border-transparent",
          className,
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent align="end">{children}</SelectContent>
    </Select>
  );
}

/** The pane's search head — a compact input with a leading glyph, plus a right slot. */
export function PaneHead({
  value,
  onChange,
  placeholder,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  children?: React.ReactNode;
}) {
  // No bottom margin: every caller sits inside `PaneBody`, whose `gap-6`
  // already sets the rhythm. The extra `mb-3` stacked on top of that gap and
  // made the search head sit half again as far from its list as every other
  // pane's head sits from its content.
  return (
    <div className="flex items-center gap-2 px-2.5">
      <div className="relative w-full max-w-72">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-n-40" />
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className={cn(paneInputClassName, "pl-8")}
        />
      </div>
      <div className="flex-1" />
      {children}
    </div>
  );
}

/** No-search-results state — states the fact, offers the way out. */
export function EmptySearch({
  query,
  noun,
  onClear,
}: {
  query: string;
  noun: string;
  onClear: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="text-base text-n-60">
        No {noun} match “{query}”.
      </p>
      <Button variant="link" size="sm" onClick={onClear} className="h-auto p-0">
        Clear search
      </Button>
    </div>
  );
}

/** The good empty state — reads as fact, not prompt. */
export function EmptyState({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="text-base text-n-60">{title}</p>
      {description ? (
        <p className="max-w-80 text-sm text-n-50">{description}</p>
      ) : null}
    </div>
  );
}

/** Keyboard chip — mono is sanctioned here (a string you'd type). */
export function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded-sm border border-n-10 bg-n-05 px-1.5 py-0.5 font-mono text-xs text-n-70">
      {children}
    </kbd>
  );
}

/** The quiet neutral status chip (design-system Badge `neutral`). */
export function NeutralBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-sm bg-n-10 px-2 py-1 text-xs font-medium leading-[1.4] text-n-70 dark:bg-n-20">
      {children}
    </span>
  );
}

/** Lifecycle badge with a leading dot — status on the lifecycle ladder. */
export function LcBadge({
  children,
  tone = "active",
}: {
  children: React.ReactNode;
  tone?: "active" | "warning" | "blocked" | "neutral";
}) {
  const color = `var(--lc-${tone})`;
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1.5 rounded-sm px-2 py-1 text-xs font-medium leading-[1.4]"
      style={{
        backgroundColor: `color-mix(in oklab, ${color} 12%, transparent)`,
        color: `color-mix(in oklab, ${color} 72%, var(--n-95))`,
      }}
    >
      <span className="size-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

/** Hover-revealed row micro-action — focus-visible always reveals (a11y). */
export function RevealBtn({
  label,
  onClick,
  destructive,
  children,
}: {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        "inline-flex size-7 shrink-0 items-center justify-center rounded-md opacity-0 transition-[opacity,color] duration-100 ease-out",
        "group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100",
        focusInset,
        destructive
          ? "text-n-30 hover:text-spectrum-red"
          : "text-n-50 hover:text-n-90",
        "[&_svg]:size-3.5",
      )}
    >
      {children}
    </button>
  );
}

/**
 * The design system's small-button register: 28px on a 4px radius. The shared
 * Button's `sm` size is 36px/6px, so every compact button in these panes wears
 * this instead.
 */
export const smButtonClassName =
  "h-7 min-h-7 rounded-sm px-3 text-sm [&_svg]:size-3 " + HOVER_T;

/** Quiet trailing action chip (design-system `chip` via token utilities). */
export const chipButtonClassName =
  "h-7 min-h-7 rounded-sm px-3 text-sm [&_svg]:size-3 bg-n-10 text-n-70 hover:bg-n-20 hover:text-n-95 " +
  // Dark's --n-10 IS the popover ground, so this fill would be invisible
  // there — step the rest and hover states up one rung.
  "dark:bg-n-20 dark:hover:bg-n-30 " +
  HOVER_T;

/** The chip's quieter companion, one rung down (design-system `chip-deep`). */
export const chipDeepButtonClassName =
  "h-7 min-h-7 rounded-sm px-3 text-sm [&_svg]:size-3 bg-n-20 text-n-60 hover:bg-n-30 hover:text-n-95 " +
  "dark:bg-n-30 dark:hover:bg-n-40 " +
  HOVER_T;

/**
 * The top-of-pane text field (invite, search). The shared Input is a fixed
 * `h-10` with `py-2`; pinning it to `h-8` crushed a ~36px content box into a
 * 32px frame. The design system anchors a *minimum* instead and lets the
 * padding set the height, so the field breathes at 12px text.
 */
export const paneInputClassName =
  "h-auto min-h-8 bg-input-background text-sm placeholder:text-n-40";

/**
 * The same anchoring one rung up, for a field stacked under its own label
 * (organization name, group name) where the type is the pane's base size.
 * `SelectSm size="field"` rides the same floor so a select never reads as a
 * shrunken sibling of the input beside it.
 */
export const paneFieldInputClassName =
  "h-auto min-h-9 bg-input-background text-base placeholder:text-n-40";

/**
 * The red chip — Log out, and nothing else so far. Same spectrum-red family
 * the account menu's Log out uses, in the filled form the rail's action stack
 * is built from.
 */
export const chipDangerButtonClassName =
  "h-7 min-h-7 rounded-sm px-3 text-sm [&_svg]:size-3 " +
  "bg-[color-mix(in_oklab,var(--spectrum-red)_10%,transparent)] text-spectrum-red " +
  "hover:bg-[color-mix(in_oklab,var(--spectrum-red)_16%,transparent)] hover:text-spectrum-red " +
  HOVER_T;

/** Outline destructive (design-system `outline-destructive` via tokens). */
export const outlineDestructiveClassName =
  "border-0 bg-transparent text-spectrum-red shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--spectrum-red)_55%,transparent)] hover:bg-[color-mix(in_oklab,var(--spectrum-red)_10%,transparent)] hover:text-spectrum-red hover:shadow-[inset_0_0_0_1px_var(--spectrum-red)]";

/** The sanctioned mono page-data foot line (provenance, ids, counts). */
export function PaneFootNote({ children }: { children: React.ReactNode }) {
  return <p className="pt-6 font-mono text-2xs text-n-40">{children}</p>;
}

/** A pane's section: mono group head + its rows, separated by space. */
export function PaneSection({
  head,
  children,
  className,
}: {
  head?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("flex flex-col gap-0.5", className)}>
      {head ? <GroupLabel>{head}</GroupLabel> : null}
      {children}
    </section>
  );
}

/** The measured column every settings pane writes into. */
export function PaneBody({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex max-w-measure flex-col gap-6", className)}>
      {children}
    </div>
  );
}
